import { useState, useEffect } from "react";
import { Button, Row, Form, Container, Spinner, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import Layout from "../../layout/default";
import Block from "../../components/Block/Block";

const MySwal = withReactContent(Swal);

function MyProfilePage() {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    address: "",
    pin_code: "",
    phone_number: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    const authToken = localStorage.getItem("authToken");

    if (userId && authToken) {
      axios
        .get(`${process.env.REACT_APP_HOST}api/users/details`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          const userDetails = response.data.data.user_details;
          setUserDetails(userDetails);
          setLoading(false);
          MySwal.fire({
            title: "Success!",
            text: "User details loaded successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch(() => {
          setError("Failed to load user details.");
          setLoading(false);
          MySwal.fire({
            title: "Error!",
            text: "Failed to load user details.",
            icon: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        });

      axios
        .get(`${process.env.REACT_APP_HOST}api/address/userAddress`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          const addressData = response.data;
          setAddresses(Array.isArray(addressData) ? addressData : [addressData]);
        })
        .catch((error) => {
          console.error("Error loading addresses:", error);
          setError("Failed to load addresses.");
          setAddresses([]);
        });
    } else {
      setError("User not found or unauthorized.");
      setLoading(false);
      MySwal.fire({
        title: "Unauthorized",
        text: "User not found or unauthorized.",
        icon: "warning",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, []);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAddress = () => {
    const authToken = localStorage.getItem("authToken");

    axios
      .post(
        `${process.env.REACT_APP_HOST}api/address`,
        newAddress,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((response) => {
        setAddresses((prevAddresses) => [...prevAddresses, response.data]);
        setShowAddressModal(false);
        setNewAddress({ address: "", pin_code: "", phone_number: "" });
        MySwal.fire({
          title: "Success!",
          text: "Address added successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message || "Failed to add address.";
        MySwal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      });
  };

  const handleEditAddress = (index) => {
    const addressToEdit = addresses[index];
    setNewAddress(addressToEdit);
    setEditAddressId(addressToEdit.id);
    setIsEdit(true);
    setShowAddressModal(true);
  };

  const handleUpdateAddress = () => {
    const authToken = localStorage.getItem("authToken");

    axios
      .put(
        `${process.env.REACT_APP_HOST}api/address/${editAddressId}`,
        newAddress,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then(() => {
        // Fetch updated addresses after successful update
        axios
          .get(`${process.env.REACT_APP_HOST}api/address/userAddress`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          .then((response) => {
            const addressData = response.data;
            setAddresses(Array.isArray(addressData) ? addressData : [addressData]);
            setShowAddressModal(false);
            setNewAddress({ address: "", pin_code: "", phone_number: "" });
            setIsEdit(false);
            setEditAddressId(null);
            MySwal.fire({
              title: "Success!",
              text: "Address updated successfully.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((error) => {
            console.error("Error refreshing addresses:", error);
            MySwal.fire({
              title: "Error!",
              text: "Failed to refresh addresses.",
              icon: "error",
              timer: 2000,
              showConfirmButton: false,
            });
          });
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message || "Failed to update address.";
        MySwal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      });
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="text-danger text-center">{error}</div>
      </Container>
    );
  }

  return (
    <Layout title="My Profile" content="container">
      <Block>
        <Row>
          <h2>User Details</h2>
          {userDetails && (
            <div>
              <p><strong>First Name:</strong> {userDetails.first_name}</p>
              <p><strong>Last Name:</strong> {userDetails.last_name}</p>
              <p><strong>Email:</strong> {userDetails.email}</p>
              <p><strong>Phone 1:</strong> {userDetails.phone_1}</p>
              <p><strong>Referral Code:</strong> {userDetails.referral_code}</p>
              <p><strong>Referred By:</strong> {userDetails.referred_by}</p>
              <p><strong>Aadhar Number:</strong> {userDetails.aadhar_number}</p>
              <p><strong>Middle Name:</strong> {userDetails.middle_name}</p>
            </div>
          )}
        </Row>
        <Row className="mt-4">
          <h3>My Addresses</h3>
          <div className="address-list">
            {addresses.length === 0 ? (
              <p>No addresses created. Create now.</p>
            ) : (
              addresses.map((address, index) => (
                <div key={index} className="address-item p-3 mb-3 border rounded">
                  <p className="mb-2"><strong>Address:</strong> {address.address}</p>
                  <p className="mb-2"><strong>Pin Code:</strong> {address.pin_code}</p>
                  <p className="mb-2"><strong>Phone:</strong> {address.phone_number}</p>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleEditAddress(index)}
                  >
                    Edit Address
                  </Button>
                </div>
              ))
            )}
          </div>
        </Row>
        <Row>
          <Button
            variant="primary"
            onClick={() => {
              setIsEdit(false);
              setShowAddressModal(true);
            }}
          >
            Add Address
          </Button>
        </Row>
      </Block>

      <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Address" : "Add Address"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="address">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={newAddress.address}
                onChange={handleAddressChange}
                placeholder="Enter address"
              />
            </Form.Group>
            <Form.Group controlId="pin_code">
              <Form.Label>Pin Code</Form.Label>
              <Form.Control
                type="text"
                name="pin_code"
                value={newAddress.pin_code}
                onChange={handleAddressChange}
                placeholder="Enter pin code"
              />
            </Form.Group>
            <Form.Group controlId="phone_number">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                name="phone_number"
                value={newAddress.phone_number}
                onChange={handleAddressChange}
                placeholder="Enter phone number"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddressModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={isEdit ? handleUpdateAddress : handleAddAddress}>
            {isEdit ? "Save Changes" : "Add Address"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="d-flex justify-content-center mt-4">
        <Button
          variant="primary"
          as={Link}
          to="/shop/auths/Edit-Profile"
          className="px-4 py-2"
          style={{ borderRadius: "25px" }}
        >
          Edit Profile
        </Button>
      </div>
    </Layout>
  );
}

export default MyProfilePage;
